import { lazy, type ComponentType } from "react";
import type { RouteComponentProps } from "wouter";
import { serviceRoutes } from "@shared/siteRoutes";
import { BlogRoute, preloadBlogRoute } from "./blogRoute";

type PageModule = { default: ComponentType<RouteComponentProps> };
type PageLoader = () => Promise<PageModule>;

export type PublicRoute = {
  path: string;
  component: ComponentType<RouteComponentProps>;
  preload: () => Promise<void>;
};

/** Keep page chunks lazy on navigation, but render an awaited landing page synchronously. */
function loadablePage(loader: PageLoader) {
  let loaded: PageModule["default"] | undefined;
  let loading: Promise<PageModule> | undefined;
  const load = () => loading ??= loader().then(module => {
    loaded = module.default;
    return module;
  }, error => {
    loading = undefined;
    throw error;
  });
  const LazyPage = lazy(load);
  const component = (props: RouteComponentProps) => {
    const Page = loaded ?? LazyPage;
    return <Page {...props} />;
  };
  return { component, preload: () => load().then(() => undefined) };
}

const NotFound = loadablePage(() => import("@/pages/NotFound"));
const Home = loadablePage(() => import("./pages/Home"));
const Sobre = loadablePage(() => import("./pages/Sobre"));
const Contactos = loadablePage(() => import("./pages/Contactos"));
const Eletricidade = loadablePage(() => import("./pages/services/Eletricidade"));
const Pichelaria = loadablePage(() => import("./pages/services/Pichelaria"));
const Carpintaria = loadablePage(() => import("./pages/services/Carpintaria"));
const Serralharia = loadablePage(() => import("./pages/services/Serralharia"));
const Pinturas = loadablePage(() => import("./pages/services/Pinturas"));
const ReabilitacoesGerais = loadablePage(() => import("./pages/services/ReabilitacoesGerais"));
const Limpezas = loadablePage(() => import("./pages/services/Limpezas"));
const AVACLimpeza = loadablePage(() => import("./pages/services/AVACLimpeza"));
const JardinsPiscinas = loadablePage(() => import("./pages/services/JardinsPiscinas"));
const Estores = loadablePage(() => import("./pages/services/Estores"));
const Eletrodomesticos = loadablePage(() => import("./pages/services/Eletrodomesticos"));
const PoliticaPrivacidade = loadablePage(() => import("./pages/PoliticaPrivacidade"));
const TermosCondicoes = loadablePage(() => import("./pages/TermosCondicoes"));
const Obrigado = loadablePage(() => import("./pages/Obrigado"));
const Planos = loadablePage(() => import("./pages/Planos"));
const ObrigadoServico = loadablePage(() => import("./pages/ObrigadoServico"));
const Grupo = loadablePage(() => import("@/pages/Grupo"));
const Blog = { component: BlogRoute, preload: preloadBlogRoute };

export const NotFoundRoute = NotFound.component;
export const notFoundRoute: PublicRoute = { path: "/404", ...NotFound };

function isConfirmationService(service: string | undefined) {
  return service === "planos" || serviceRoutes.some(route => route.slug === service);
}

// The original page renders this same NotFound component for invalid services.
// Selecting it here lets a missing URL preload only NotFound, not the form-confirmation chunk.
function ConfirmationRoute(props: RouteComponentProps) {
  const Page = isConfirmationService(props.params.service)
    ? ObrigadoServico.component
    : NotFoundRoute;
  return <Page {...props} />;
}

/** The single client manifest for public route patterns, components and page loaders. */
export const publicRoutes: readonly PublicRoute[] = [
  { path: "/", ...Home },
  { path: "/sobre", ...Sobre },
  { path: "/contactos", ...Contactos },
  { path: "/blog", ...Blog },
  { path: "/blog/:slug", ...Blog },
  { path: "/servicos/reabilitacoes-gerais", ...ReabilitacoesGerais },
  { path: "/servicos/eletricidade", ...Eletricidade },
  { path: "/servicos/pichelaria", ...Pichelaria },
  { path: "/servicos/carpintaria", ...Carpintaria },
  { path: "/servicos/serralharia", ...Serralharia },
  { path: "/servicos/pinturas", ...Pinturas },
  { path: "/servicos/limpezas", ...Limpezas },
  { path: "/servicos/avac-limpeza", ...AVACLimpeza },
  { path: "/servicos/jardins-piscinas", ...JardinsPiscinas },
  { path: "/servicos/estores", ...Estores },
  { path: "/servicos/eletrodomesticos", ...Eletrodomesticos },
  { path: "/politica-de-privacidade", ...PoliticaPrivacidade },
  { path: "/termos", ...TermosCondicoes },
  { path: "/termos-e-condicoes", ...TermosCondicoes },
  { path: "/obrigado", ...Obrigado },
  { path: "/planos", ...Planos },
  { path: "/obrigado/:service", ...ObrigadoServico, component: ConfirmationRoute },
  { path: "/grupo", ...Grupo },
  notFoundRoute,
];

// Never preloaded or rendered by the server entry; keep private dependencies out of SSR.
export const gatedRoutes = [
  { path: "/admin", component: lazy(() => import("@/pages/Admin")) },
  { path: "/admin/login", component: lazy(() => import("@/pages/AdminLogin")) },
] as const;

/** Mirror Wouter's decodeURI (not decodeURIComponent), preserving reserved escapes. */
export function normalizeRoutePath(url: string): string {
  let pathname = url.split("?")[0];
  try { pathname = decodeURI(pathname); } catch { /* Malformed escape: retain the raw path. */ }
  return pathname.replace(/\/+$/, "") || "/";
}

const routeMatchers = publicRoutes.map(route => ({
  route,
  pattern: new RegExp(`^${route.path.replace(/:[^/]+/g, "[^/]+")}$`, "i"),
}));

/** Undefined means gated (no preload); all unknown public URLs resolve to NotFound. */
export function resolvePublicRoute(url: string): PublicRoute | undefined {
  const pathname = normalizeRoutePath(url);
  if (/^\/admin(?:\/|$)/i.test(pathname)) return undefined;
  const confirmation = /^\/obrigado\/([^/]+)$/i.exec(pathname);
  if (confirmation && !isConfirmationService(confirmation[1])) return notFoundRoute;
  return routeMatchers.find(({ pattern }) => pattern.test(pathname))?.route ?? notFoundRoute;
}

export async function preloadPublicRoute(url: string): Promise<void> {
  await resolvePublicRoute(url)?.preload();
}
